import { useSearchParams } from "react-router-dom";
import { deriveReceiptStatus } from "@/components/ReceiptStatusBadge";

/**
 * useExpensesFilters
 *
 * Centralises the URL-driven filter state for the Expenses page:
 *   - `missing_receipt=1`  Legacy mine-only-missing-receipt link from
 *                          dashboards / notifications.
 *   - `category=...`       One of the canonical category keys; "all"
 *                          (or absent) means no filter.
 *   - `no_receipts=1`      Hides any expense that already has a receipt,
 *                          regardless of payer.
 *
 * Extracted from pages/Expenses.jsx in the v77.99.19 Phase 3 frontend
 * decomposition pass. Behaviour preserved verbatim — the same query
 * strings, the same filter composition, the same `visibleItems`
 * predicate (so a deep link from `/dashboard` keeps showing the same
 * rows).
 *
 * Args:
 *  - items:   raw expense list
 *  - userId:  current user's id (for the mine-only branch of the
 *             legacy missing-receipt path)
 *
 * Returns:
 *  {
 *    visibleItems,    filtered list ready for rendering
 *    missingOnly,     bool – the legacy filter is active
 *    categoryFilter,  string – current category key or "all"
 *    noReceiptsOnly,  bool
 *    filtersActive,   bool – at least one user-toggleable filter is on
 *    clearMissingFilter, setCategoryFilter, toggleNoReceipts, clearAllFilters,
 *  }
 */
export function useExpensesFilters(items, userId) {
  const [searchParams, setSearchParams] = useSearchParams();
  const missingOnly = searchParams.get("missing_receipt") === "1";
  const categoryFilter = searchParams.get("category") || "all";
  const noReceiptsOnly = searchParams.get("no_receipts") === "1";
  // v77.99.107 — Status filter. URL-driven so deep links from
  // dashboards/insights ("Show me settled expenses this year") work.
  // Allowed values: "all" (default), "outstanding" (status==outstanding
  // OR pending_confirmation), "partial", "paid", "voided".
  const statusFilter = searchParams.get("status") || "all";

  const visibleItems = items.filter((x) => {
    if (missingOnly) {
      if (x.voided) return false;
      if (x.paid_by !== userId) return false;
      if (deriveReceiptStatus(x).status !== "missing") return false;
    }
    if (categoryFilter !== "all" && (x.category || "other") !== categoryFilter) {
      return false;
    }
    if (noReceiptsOnly && deriveReceiptStatus(x).status !== "missing") {
      return false;
    }
    // v77.99.112 — Effective status with credit allocations applied.
    // An expense is "effectively settled" once the sum of its credit
    // allocations covers the outstanding_to_payer (even though the
    // raw `status` field on the doc remains "outstanding" — we never
    // flip the doc itself, to keep the audit trail clean). Similarly,
    // a partially-covered expense is "effectively partial".
    const _allocs = x.credit_allocations || [];
    const _allocSum = _allocs.reduce((s, a) => s + (a.amount || 0), 0);
    const _outstanding = (x.breakdown?.outstanding_to_payer || 0);
    const _coveredByCredit = _allocSum > 0 && _outstanding - _allocSum <= 0.005;
    const _partlyCoveredByCredit = _allocSum > 0 && !_coveredByCredit && (x.status === "outstanding" || x.status === "pending_confirmation");
    // Status filter — "all" (default) shows everything including
    // voided (legacy behaviour preserved). Other values match the
    // requested status explicitly. v77.99.112 — Settled and Part-paid
    // also pick up rows fully (or partially) covered by credit
    // allocations, mirroring the headline "Net still owed" math.
    if (statusFilter === "voided") {
      if (!x.voided) return false;
    } else if (statusFilter === "outstanding") {
      if (x.voided) return false;
      if (_coveredByCredit) return false;
      if (_partlyCoveredByCredit) return false;
      if (x.status !== "outstanding" && x.status !== "pending_confirmation") return false;
    } else if (statusFilter === "partial") {
      if (x.voided) return false;
      if (x.status === "partial") return true;
      if (_partlyCoveredByCredit) return true;
      return false;
    } else if (statusFilter === "paid") {
      if (x.voided) return false;
      if (x.status === "paid") return true;
      if (_coveredByCredit) return true;
      return false;
    }
    return true;
  });

  const clearMissingFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("missing_receipt");
    setSearchParams(next, { replace: true });
  };
  const setCategoryFilter = (v) => {
    const next = new URLSearchParams(searchParams);
    if (!v || v === "all") next.delete("category");
    else next.set("category", v);
    setSearchParams(next, { replace: true });
  };
  const toggleNoReceipts = () => {
    const next = new URLSearchParams(searchParams);
    if (noReceiptsOnly) next.delete("no_receipts");
    else next.set("no_receipts", "1");
    setSearchParams(next, { replace: true });
  };
  const setStatusFilter = (v) => {
    const next = new URLSearchParams(searchParams);
    if (!v || v === "all") next.delete("status");
    else next.set("status", v);
    setSearchParams(next, { replace: true });
  };
  const filtersActive = categoryFilter !== "all" || noReceiptsOnly || statusFilter !== "all";
  const clearAllFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("category");
    next.delete("no_receipts");
    next.delete("status");
    setSearchParams(next, { replace: true });
  };

  return {
    visibleItems,
    missingOnly,
    categoryFilter,
    noReceiptsOnly,
    statusFilter,
    filtersActive,
    clearMissingFilter,
    setCategoryFilter,
    toggleNoReceipts,
    setStatusFilter,
    clearAllFilters,
  };
}
